import { useState } from 'react';
import axios from 'axios';
import { Mail, BellRing, Check, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PhoneInput } from './PhoneInput';
import { nationalDigits, formatIndianMobile } from '../lib/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotifyMeForm({ slug, launch = false }) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const digits = nationalDigits(phone);
    if (digits.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/stock/notify-me`, {
        product_slug: slug,
        phone: phone.trim(),
        email: email.trim() || null,
        name: name.trim() || null,
      });
      setDone(true);
      toast.success(launch ? "You're on the list — we'll WhatsApp you the moment it launches." : "You're on the list — we'll WhatsApp you the moment it's back.");
    } catch {
      toast.error('Could not save. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#111214] border border-emerald-900/40 rounded-xl p-5 flex items-center gap-3" data-testid="notify-me-success">
        <Check className="w-5 h-5 text-emerald-400 shrink-0" />
        <div>
          <div className="text-white font-medium text-sm">You're on the list.</div>
          <div className="text-xs text-zinc-400">
            We'll WhatsApp <span className="text-white">{formatIndianMobile(phone)}</span>
            {email.trim() && <> and email <span className="text-white">{email}</span></>} {launch ? 'the moment this paddle launches.' : 'the moment this paddle is back in stock.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-[#111214] border border-zinc-800 rounded-xl p-5 space-y-3"
      data-testid="notify-me-form"
    >
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-red-400" />
        <h4 className="text-white font-semibold">{launch ? 'Notify me at launch' : "Notify me when it's back"}</h4>
      </div>
      <p className="text-xs text-zinc-400">Drop your phone number — we'll WhatsApp you the moment it {launch ? 'launches' : "'s restocked"}. No spam, ever.</p>
      <div className="relative">
        <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
        <PhoneInput
          required
          value={phone}
          onChange={setPhone}
          placeholder="WhatsApp number"
          className="w-full bg-[#0B0B0C] border border-zinc-800 rounded-lg pl-9 pr-3 py-3 text-sm text-white focus-within:border-zinc-600"
          inputClassName="placeholder:text-zinc-600"
          testId="notify-me-phone"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (optional)"
          className="w-full bg-[#0B0B0C] border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-600 text-white"
          data-testid="notify-me-name"
        />
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            className="w-full bg-[#0B0B0C] border border-zinc-800 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-zinc-600 text-white"
            data-testid="notify-me-email"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={busy}
        className="w-full px-5 h-11 font-semibold bg-white text-black rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="notify-me-submit"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Notify me
      </button>
    </form>
  );
}
